import { useWeb3Provider } from '@trustpad/launchpad';
import { useStakingProvider } from '@trustpad/launchpad/dist/staking/hooks/useStaking';
import * as React from 'react';
import { useEffect, useState } from 'react';
import CountUp from 'react-countup';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import { toBN } from 'web3-utils';
import { Countdown } from '~common/Countdown';
import { ZERO_BN } from '~config/constants';
import { BuyPancakeSwapButton } from '~features/launched';
import { AdminPanel } from '~features/staking/components/AdminPanel';
import { ClaimCard } from '~features/staking/components/ClaimCard';
import { DepositCard } from '~features/staking/components/DepositCard';
import { LiquidityProvideLink } from '~features/staking/components/LiquidityProvideLink';
import { StakingTitle } from '~features/staking/components/StakingCard/StakingTitle';
import { WithdrawCard } from '~features/staking/components/WithdrawCard';
import { getStakingSymbol, getStakingTokens } from '~features/staking/utils';
import { fNum, toEthN } from '~utils';

function useLPAmountInToken(amount) {
  const { web3 } = useWeb3Provider();
  const { stakingContract } = useStakingProvider();
  const [tokenAmount, setAmount] = useState(ZERO_BN);

  useEffect(() => {
    if (stakingContract) {
      const instance = new web3.eth.Contract(
        [
          {
            inputs: [
              {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
              },
            ],
            name: 'getLPTokenValue',
            outputs: [
              {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
              },
            ],
            stateMutability: 'view',
            type: 'function',
          },
        ],
        stakingContract.options.address,
      );
      instance.methods
        .getLPTokenValue(amount)
        .call()
        .then(toBN)
        .then(setAmount);
    }
  }, [stakingContract, amount, web3]);

  return tokenAmount;
}

export function StakingLPCard() {
  const { pool, userStats } = useStakingProvider();
  const [collapsed, setCollapsed] = useState(true);
  const [mainToken, secondToken] = getStakingTokens(pool);
  const lpAmount = useLPAmountInToken(userStats.lpBalance);

  return (
    <div className="overflow-hidden">
      <div
        className={`px-6 py-3 bg-dark1 bg-opacity-80 border-dark1 shadow-2xl cursor-pointer ${
          collapsed ? 'rounded-lg' : 'rounded-t-lg'
        }`}
        onClick={() => setCollapsed(!collapsed)}>
        <div className="flex items-top flex-col md:flex-row">
          <div className="mr-6 flex flex-col gap-6">
            <TokenImage
              imageSrc={mainToken.imageSrc}
              symbol={mainToken.symbol}
              secondImageSrc={secondToken?.imageSrc}
            />

            <div className="flex flex-col gap-3">
              <BuyPancakeSwapButton />
              <LiquidityProvideLink
                mainToken={mainToken}
                secondToken={secondToken}
              />
            </div>
          </div>

          <div className="space-y-3 flex-grow">
            <StakingTitle pool={pool} />

            {pool.description && (
              <div className="space-y-2">{pool.description()}</div>
            )}
            <div>
              Stake TPAD-BNB CAKE LP tokens to earn.{' '}
              {lpAmount.gtn(0) && (
                <span>
                  Your current LP balance worth is {toEthN(lpAmount)}{' '}
                  {mainToken.symbol} tokens.
                </span>
              )}
            </div>

            {pool.deprecated && (
              <div>
                Pool is deprecated, please withdraw the funds and stake in the
                other one.
              </div>
            )}

            <div className="w-full">
              <StakingStats
                priceLabel={secondToken ? 'LP' : mainToken.symbol}
                forceAprZero={pool.deprecated}
              />
            </div>
          </div>

          <div className="ml-auto px-6">
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="text-3xl">
              {collapsed ? <FaChevronDown /> : <FaChevronUp />}
            </button>
          </div>
        </div>

        <div className="absolute left-full w-full top-0">
          <AdminPanel />
        </div>
      </div>

      <ActionsCard
        collapsed={collapsed}
        symbol={getStakingSymbol(pool)}
        lockDeposit={pool.deprecated}
        lockWithdraw={false}
        deprecated={pool.deprecated}>
        <div></div>
      </ActionsCard>
    </div>
  );
}

function StakingStats({
  priceLabel,
  forceAprZero,
}: {
  priceLabel: string;
  forceAprZero: boolean;
}) {
  const { totalStats, userStats, totalValue, pool } = useStakingProvider();
  const { lpDeposited, pendingReward } = userStats;

  return (
    <div className="flex justify-around items-center flex-wrap md:flex-nowrap">
      <Item label="Staked">
        {fNum(lpDeposited)} {priceLabel}
      </Item>
      {false && (
        <Item label="Rewards per minute">
          {123} {pool.rewardToken.symbol}
        </Item>
      )}

      <div className="flex flex-col items-center md:w-1/5 py-3 -m-3">
        <div className="flex items-center text-lg bg-gradient-to-b from-green-700/50 rounded-2xl p-3">
          <span className="brand-text text-4xl min-w-12">
            {forceAprZero ? (
              0
            ) : totalStats.apr > 0 ? (
              <>
                <CountUp end={totalStats.apr / 2} preserveValue />%
              </>
            ) : (
              <Skeleton className="w-12" />
            )}
          </span>
        </div>
        <div className="text-sm opacity-50">APR</div>
      </div>

      <Item label="Earned">
        <span>
          <CountUp end={toEthN(pendingReward)} decimals={2} preserveValue />{' '}
          {pool.rewardToken.symbol}
        </span>
      </Item>
    </div>
  );
}

function Item({ label, children }) {
  return (
    <div className="flex flex-col items-center md:w-1/5 py-3">
      <div className="flex items-center text-lg">{children}</div>
      <div className="text-sm opacity-50">{label}</div>
    </div>
  );
}

function ActionsCard({
  collapsed,
  symbol,
  lockDeposit = false,
  lockWithdraw = false,
  deprecated = false,
  children,
}: {
  collapsed: boolean;
  symbol: string;
  lockDeposit: boolean;
  lockWithdraw: boolean;
  deprecated: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`duration-300 transition-height transform ${
        collapsed ? 'h-0' : 'h-full md:h-64'
      }`}>
      <div className="bg-dark1 bg-opacity-80 rounded-b-lg shadow-lg">
        {children}

        <div
          className={`flex justify-items-stretch p-3 border-gray-500 border-opacity-30 
        border-t-2 flex-col md:flex-row space-y-3 md:space-y-0`}>
          <div className="md:w-1/3 p-3 order-2 md:order-1">
            <DepositCard symbol={symbol} lockDeposit={lockDeposit} />
          </div>
          <div className="md:w-1/3 p-3 order-1 md:order-2">
            <WithdrawCard symbol={symbol} lockWithdraw={lockWithdraw} />
          </div>
          {!(deprecated && symbol === 'LTY/BUSD') && (
            <div className="md:w-1/3 p-3 order-0 md:order-3">
              <ClaimCard />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TokenImage({
  imageSrc,
  symbol,
  secondImageSrc,
}: {
  imageSrc: string;
  symbol: string;
  secondImageSrc?: string;
}) {
  return (
    <div className="h-20 w-20 mx-auto inline-block rounded-full p-1 brand-bg">
      <div className="bg-darkGray rounded-full relative">
        <img src={imageSrc} alt={symbol} className="h-full rounded-full" />
        {secondImageSrc && (
          <img
            src={secondImageSrc}
            alt="Pair token"
            className="h-8 absolute rounded-full bg-darkGray/90 p-1 bottom-0 right-0"
          />
        )}
      </div>
    </div>
  );
}
